// partyCallCalendarHelpers.js
// Utility helpers for computing calendar weeks and monthly summaries across any month or year

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const DAYS_OF_WEEK = [
  { name: "SUNDAY", short: "Sun", isWeekend: true },
  { name: "MONDAY", short: "Mon", isWeekend: false },
  { name: "TUESDAY", short: "Tue", isWeekend: false },
  { name: "WEDNESDAY", short: "Wed", isWeekend: false },
  { name: "THURSDAY", short: "Thu", isWeekend: false },
  { name: "FRIDAY", short: "Fri", isWeekend: false },
  { name: "SATURDAY", short: "Sat", isWeekend: false },
];

/**
 * Computes calendar matrix (weeks, days with calls, monthStats) for any given month & year
 */
export const computeMonthCalendarData = (filteredCalls = [], year, month) => {
  const targetYear = Number(year);
  const targetMonth = Number(month);

  // 1. Filter calls belonging to this month & year
  const callsForMonth = (filteredCalls || []).filter((c) => {
    if (!c.Date) return false;
    const d = new Date(c.Date);
    if (isNaN(d.getTime())) return false;
    return d.getFullYear() === targetYear && d.getMonth() === targetMonth;
  });

  // 2. Group calls by day number and calculate statistics
  const callsByDay = {};
  let completed = 0;
  let pending = 0;
  let other = 0;

  callsForMonth.forEach((c) => {
    const d = new Date(c.Date);
    const day = d.getDate();
    if (!callsByDay[day]) callsByDay[day] = [];
    callsByDay[day].push(c);

    const s = (c.Status || "").toLowerCase().trim();
    if (s === "completed") completed++;
    else if (s === "pending") pending++;
    else other++;
  });

  const monthStats = {
    total: callsForMonth.length,
    completed,
    pending,
    other,
    inProgress: other,
    resolutionRate:
      callsForMonth.length > 0
        ? `${Math.round((completed / callsForMonth.length) * 100)}%`
        : "100%",
  };

  // 3. Generate calendar days array
  const firstDayOfWeek = new Date(targetYear, targetMonth, 1).getDay(); // 0 is Sunday
  const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
  const daysInPrevMonth = new Date(targetYear, targetMonth, 0).getDate();

  const days = [];

  // Leading padding days from previous month
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    days.push({
      dayNumber: daysInPrevMonth - i,
      isCurrentMonth: false,
      date: new Date(targetYear, targetMonth - 1, daysInPrevMonth - i),
      calls: [],
    });
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      dayNumber: i,
      isCurrentMonth: true,
      date: new Date(targetYear, targetMonth, i),
      calls: callsByDay[i] || [],
    });
  }

  // Trailing padding days to fill 7 columns
  const totalSlots = Math.ceil(days.length / 7) * 7;
  const remainingSlots = totalSlots - days.length;
  for (let i = 1; i <= remainingSlots; i++) {
    days.push({
      dayNumber: i,
      isCurrentMonth: false,
      date: new Date(targetYear, targetMonth + 1, i),
      calls: [],
    });
  }

  // 4. Group days into 7-day weeks
  const calendarWeeks = [];
  for (let i = 0; i < days.length; i += 7) {
    const weekDays = days.slice(i, i + 7);
    const hasCalls = weekDays.some((d) => d.calls && d.calls.length > 0);
    const totalCalls = weekDays.reduce(
      (sum, d) => sum + (d.calls ? d.calls.length : 0),
      0
    );
    calendarWeeks.push({
      weekIndex: i / 7,
      days: weekDays,
      hasCalls,
      totalCalls,
    });
  }

  return {
    month: targetMonth,
    year: targetYear,
    monthName: MONTH_NAMES[targetMonth] || "",
    shortName: (MONTH_NAMES[targetMonth] || "").slice(0, 3),
    callsForMonth,
    callsByDay,
    monthStats,
    calendarWeeks,
    hasCalls: callsForMonth.length > 0,
  };
};

/**
 * Computes full 12 months calendar data for an entire year
 */
export const computeAllMonthsCalendarData = (filteredCalls = [], year) => {
  const months = [];
  for (let m = 0; m < 12; m++) {
    months.push(computeMonthCalendarData(filteredCalls, year, m));
  }
  return months;
};
